import { IIssueBoardApi, IIssueBoardBase, IIssueBoardDBSchema } from "./schemas/index.js";
import { IOtherAttributesFormula } from "./schemas/issueBoardSchema.js";

type StringKeyStringValueObject = {
  [key: string]: string;
};

export interface IAllOtherAttributeFormulaWithGeneratedStrings {
  [issueId: string]: StringKeyStringValueObject;
}

type IssueCountEntry = {
  issueCounts: {
    [key: string]: number;
  };
};

export interface ICountersForAllAttributesWithFormula {
  [attributeKey: string]: IssueCountEntry[];
}

export interface IFormulaIssue {
  id?: string | number;
  taskOrIssue?: "TASK" | "ISSUE";
  otherAttributesData?: Record<string, unknown> | null;
  formulaCounters?: Record<string, { value?: number | null } | undefined> | null;
}

type FormulaOtherAttributeConfigItem = {
  typeOfValue?: string;
  formula?: IOtherAttributesFormula | null;
  isActive?: boolean;
};

type FormulaOtherAttributesConfig =
  | Record<string, FormulaOtherAttributeConfigItem>
  | Map<string, FormulaOtherAttributeConfigItem>;

interface IArgsFormatIssueFormulaGeneratedString {
  formula: IOtherAttributesFormula;
  countForIssue: number | null;
  deletePreviousDelimiterIfAttributeIsEmpty: boolean;
  issue: Partial<IFormulaIssue>;
  otherAttributesConfig: FormulaOtherAttributesConfig;
}

interface IArgGeneratedStringsForFormula {
  issueBoard: IIssueBoardBase | IIssueBoardApi | IIssueBoardDBSchema;
  countersForIssuesOnAllKeys?: ICountersForAllAttributesWithFormula;
  issues: IFormulaIssue[];
}

interface IArgGetIssuesWithFormulaAdded<TIssue extends IFormulaIssue> {
  issues: TIssue[];
  issueBoard: IIssueBoardBase | IIssueBoardApi | IIssueBoardDBSchema;
  sumsByFormula?: ICountersForAllAttributesWithFormula;
}

const formatShortDate = (dateStr: unknown): string => {
  if (dateStr == null) {
    return "";
  }

  const date = new Date(String(dateStr));

  if (Number.isNaN(date.getTime())) {
    return String(dateStr);
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${day}.${month}.${year}`;
};

const getStringFromNrOfCharsInFormula = (nrOfChars: number | undefined, value: number): string => {
  const minLength = nrOfChars ?? 3;
  const valueAsString = value.toString();
  if (valueAsString.length >= minLength) {
    return valueAsString;
  }

  return "0".repeat(minLength - valueAsString.length) + valueAsString;
};

const mergeIssueCounts = (data: IssueCountEntry[]): { [key: string]: number } => {
  return data.reduce((acc, entry) => {
    Object.entries(entry.issueCounts).forEach(([key, value]) => {
      acc[key] = value;
    });
    return acc;
  }, {} as { [key: string]: number });
};

const getOtherAttributesConfigRecord = (
  otherAttributesConfig: FormulaOtherAttributesConfig | null | undefined,
): Record<string, FormulaOtherAttributeConfigItem> => {
  if (otherAttributesConfig == null) {
    return {};
  }

  if (otherAttributesConfig instanceof Map) {
    return Object.fromEntries(otherAttributesConfig);
  }

  return otherAttributesConfig;
};

export const formatIssueFormulaGeneratedString = (args: IArgsFormatIssueFormulaGeneratedString): string => {
  const config = getOtherAttributesConfigRecord(args.otherAttributesConfig);

  return args.formula.confArr.reduce((acc, conf) => {
    if (conf.type === "DELIMITER") {
      acc += conf.value;
    }

    if (conf.type === "ATTRIBUTE") {
      const issueRecord = args.issue as Record<string, unknown>;
      const value = args.issue.otherAttributesData?.[conf.value] ?? issueRecord[conf.value];

      if (value != null && value !== "") {
        acc += config[conf.value]?.typeOfValue === "DATE" ? formatShortDate(value) : String(value);
      } else if (args.deletePreviousDelimiterIfAttributeIsEmpty) {
        const previousConf = args.formula.confArr[args.formula.confArr.indexOf(conf) - 1];
        if (acc.length > 0 && previousConf?.type === "DELIMITER") {
          acc = acc.slice(0, -previousConf.value.length);
        }
      }
    }

    if (conf.type === "COUNTER") {
      if (args.countForIssue == null) {
        const previousConf = args.formula.confArr[args.formula.confArr.indexOf(conf) - 1];

        if (acc.length > 0 && previousConf?.type === "DELIMITER") {
          acc = acc.slice(0, -previousConf.value.length);
        }

        return acc;
      }

      acc += getStringFromNrOfCharsInFormula(args.formula.nrOfCharsInCounter, args.countForIssue);
    }

    return acc;
  }, "");
};

export const getGeneratedStringForFormulaOnAllKeys = (
  args: IArgGeneratedStringsForFormula,
): IAllOtherAttributeFormulaWithGeneratedStrings => {
  const generatedStrings: IAllOtherAttributeFormulaWithGeneratedStrings = {};
  const otherAttributesConfig = getOtherAttributesConfigRecord(
    args.issueBoard?.otherAttributesConfig as FormulaOtherAttributesConfig | null | undefined,
  );

  if (args.issues == null) {
    return generatedStrings;
  }

  const formulaAttributes = Object.entries(otherAttributesConfig).filter(
    ([, attribute]) => attribute.typeOfValue === "FORMULA" && attribute.formula != null,
  );

  const issueCountsByFormulaKey = formulaAttributes.reduce<Record<string, Record<string, number>>>(
    (acc, [key, attribute]) => {
      if (attribute.formula?.counterMode !== "MANUAL") {
        acc[key] = mergeIssueCounts(args.countersForIssuesOnAllKeys?.[key] || []);
      }

      return acc;
    },
    {},
  );

  args.issues.forEach((issue) => {
    if (issue == null) return;
    if (issue.taskOrIssue === "TASK") return;

      const issueId = issue.id;
      if (issueId == null) return;

      const otherAttributesData = issue.otherAttributesData;

    formulaAttributes.forEach(([key, attribute]) => {
      const formula = attribute.formula;
      if (formula == null) return;

      const manualCounterValue = formula.counterMode === "MANUAL" ? issue.formulaCounters?.[key]?.value : null;
      const generatedString = formatIssueFormulaGeneratedString({
        formula,
        countForIssue:
          formula.counterMode === "MANUAL" ? manualCounterValue ?? null : issueCountsByFormulaKey[key]?.[issueId] ?? 0,
        deletePreviousDelimiterIfAttributeIsEmpty: formula.deletePreviousDelimiterIfAttributeIsEmpty ?? false,
        issue: { ...issue, otherAttributesData },
        otherAttributesConfig,
      });
      generatedStrings[issueId] = generatedStrings[issueId] || {};
      generatedStrings[issueId][key] = generatedString;
    });
  });

  return generatedStrings;
};

export const issueBoardContainsFormula = (
  issueBoard: IIssueBoardBase | IIssueBoardApi | IIssueBoardDBSchema | null | undefined,
): boolean => {
  return Object.values(
    getOtherAttributesConfigRecord(issueBoard?.otherAttributesConfig as FormulaOtherAttributesConfig | null | undefined),
  ).some(
    (attribute) => attribute.typeOfValue === "FORMULA",
  );
};

export const issueBoardContainsAutomaticFormula = (
  issueBoard: IIssueBoardBase | IIssueBoardApi | IIssueBoardDBSchema | null | undefined,
): boolean => {
  return Object.values(
    getOtherAttributesConfigRecord(issueBoard?.otherAttributesConfig as FormulaOtherAttributesConfig | null | undefined),
  ).some(
    (attribute) =>
      attribute.typeOfValue === "FORMULA" &&
      attribute.isActive === true &&
      attribute.formula?.counterMode !== "MANUAL",
  );
};

export const getIssuesWithFormulaAdded = <TIssue extends IFormulaIssue>(
  args: IArgGetIssuesWithFormulaAdded<TIssue>,
): TIssue[] => {
  if (args.issues == null || args.issueBoard == null) {
    return args.issues;
  }

  try {
    const issuesFormulas = getGeneratedStringForFormulaOnAllKeys({
      issueBoard: args.issueBoard,
      countersForIssuesOnAllKeys: args.sumsByFormula,
      issues: args.issues,
    });

    return args.issues.map((issue) => {
      const issueId = issue.id;
      if (issueId == null) {
        return { ...issue } as TIssue;
      }

      if (issuesFormulas[issueId] != null) {
        return {
          ...issue,
          otherAttributesData: {
            ...issue.otherAttributesData,
            ...issuesFormulas[issueId],
          },
        } as TIssue;
      }
      return { ...issue } as TIssue;
    });
  } catch (error) {
    console.error("Error in getIssuesWithFormulaAdded:", error);
    return args.issues;
  }
};
